import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { FileText, Clock, User as UserIcon, RefreshCw, History as HistoryIcon, Plus, Search, X, Check, Trash, Calendar as CalendarIcon, GripVertical, ChevronUp, ChevronDown, Save, RotateCcw, Hash, Globe, Star, ArrowUp, ArrowDown, Edit, Archive, Upload, Image as ImageIcon, Video, Music, Play, Pause } from 'lucide-react';
import { deckService, newsService } from '../../../services';
import { shortenEmail } from '../../../utils/email';
import Loader from '../../../components/common/Loader/Loader';
import NewsCard from './NewsCard';
import './DeckDetail.css';

// Deck Card Media Carousel Component
const DeckCardMedia = ({ assets, coverType }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const carouselRef = useRef(null);
  const intervalRef = useRef(null);

  // Get valid assets (filter out null/undefined)
  const validAssets = assets.filter(a => a !== null && a !== undefined);

  // Auto-slide for multiple images
  useEffect(() => {
    if (validAssets.length > 1 && !isPaused) {
      intervalRef.current = setInterval(() => {
        setCurrentIndex(prev => (prev + 1) % validAssets.length);
      }, 1500);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [validAssets.length, isPaused]);

  // Reset index when assets change
  useEffect(() => {
    setCurrentIndex(0);
  }, [assets]);

  const handleDotClick = (index) => {
    setCurrentIndex(index);
    // Pause briefly when manually changing slide
    setIsPaused(true);
    setTimeout(() => setIsPaused(false), 5000);
  };

  const handleMouseEnter = () => {
    setIsPaused(true);
  };

  const handleMouseLeave = () => {
    setIsPaused(false);
  };

  const currentAsset = validAssets[currentIndex];

  // Show no preview placeholder if no assets
  if (!currentAsset || coverType === 'none' || !coverType) {
    return (
      <div className="deck-card-media deck-card-media--no-preview">
        <div className="deck-card-no-preview">
          <ImageIcon size={24} />
          <span className="deck-card-no-preview-text">None</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className="deck-card-media"
      ref={carouselRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="deck-card-media-content">
        {coverType === 'image' && currentAsset.type === 'IMAGE' && (
          <img src={currentAsset.source_url} alt="Deck cover" className="deck-card-media-image" />
        )}
        {coverType === 'video' && currentAsset.type === 'VIDEO' && (
          <div className="deck-card-media-video">
            <Video size={20} />
          </div>
        )}
        {coverType === 'audio' && currentAsset.type === 'AUDIO' && (
          <div className="deck-card-media-audio">
            <Music size={20} />
          </div>
        )}

        {/* Carousel indicators for multiple images */}
        {validAssets.length > 1 && (
          <div className="deck-card-carousel-dots">
            {validAssets.map((_, index) => (
              <button
                key={index}
                className={`deck-card-carousel-dot ${index === currentIndex ? 'deck-card-carousel-dot--active' : ''}`}
                onClick={() => handleDotClick(index)}
                aria-label={`Slide ${index + 1}`}
              />
            ))}
          </div>
        )}

        {/* Pause/Play indicator for carousel */}
        {validAssets.length > 1 && (
          <div className="deck-card-carousel-indicator">
            {isPaused ? <Pause size={10} /> : <Play size={10} />}
          </div>
        )}
      </div>

      {/* Asset text overlay */}
      {currentAsset.text && (
        <div className="deck-card-media-text">
          {currentAsset.text}
        </div>
      )}
    </div>
  );
};

const DeckDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [deck, setDeck] = useState(null);
  const [currentDeckId, setCurrentDeckId] = useState(id);
  const [newsList, setNewsList] = useState([]);
  const [selectedNews, setSelectedNews] = useState(null);
  const [newsDecks, setNewsDecks] = useState({});
  const [loading, setLoading] = useState(true);
  const [newsListLoading, setNewsListLoading] = useState(false);
  const [error, setError] = useState(null);
  const [columnHeight, setColumnHeight] = useState(null);
  const middleColumnRef = useRef(null);
  const lastProcessedDeckId = useRef(null);
  const lastProcessedNewsId = useRef(null);

  // Add News Mode states
  const [isAddNewsMode, setIsAddNewsMode] = useState(false);
  const [newsUrl, setNewsUrl] = useState('');
  const [newsSearch, setNewsSearch] = useState('');
  const [generatingNews, setGeneratingNews] = useState(false);
  const [generatedNews, setGeneratedNews] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [addingNewsId, setAddingNewsId] = useState(null);
  const [addingGeneratedNews, setAddingGeneratedNews] = useState(false);
  const [confirmRemoveNewsId, setConfirmRemoveNewsId] = useState(null);
  const rightColumnRef = useRef(null);
  const [searchResultsDecks, setSearchResultsDecks] = useState({});
  const [newsGenerationError, setNewsGenerationError] = useState(null);
  // Set while the article is being fetched by the browser because the
  // publisher's CDN refuses our server
  const [newsGenerationNotice, setNewsGenerationNotice] = useState(null);

  // Reorder Mode states
  const [originalNewsOrder, setOriginalNewsOrder] = useState([]);
  const [currentNewsOrder, setCurrentNewsOrder] = useState([]);
  const [savingOrder, setSavingOrder] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [isMobileReorderMode, setIsMobileReorderMode] = useState(false);
  const [clickedNewsId, setClickedNewsId] = useState(null);
  const [reorderingItem, setReorderingItem] = useState(null);

  // Deck action states
  const [creatingDeck, setCreatingDeck] = useState(false);
  const [publishingDeck, setPublishingDeck] = useState(false);
  const [updatingDeck, setUpdatingDeck] = useState(false);
  const [deletingDeck, setDeletingDeck] = useState(false);
  const [showDeckDeleteConfirm, setShowDeckDeleteConfirm] = useState(false);
  const [createDeckError, setCreateDeckError] = useState(null);

  // Publish form states
  const [showPublishForm, setShowPublishForm] = useState(false);
  const [publishPosition, setPublishPosition] = useState('');
  const [publishDurationFromDateTime, setPublishDurationFromDateTime] = useState('');
  const [publishDurationToDateTime, setPublishDurationToDateTime] = useState('');

  // Helper to check if news is in current deck
  const isNewsInDeck = (newsId) => {
    return newsList.some(news => news._id === newsId);
  };

  // Handle deck selection from NewsCard dropdown
  const handleDeckSelect = async (deckId) => {
    // Keep the currently selected news to show in middle column
    const currentNewsId = selectedNews?._id;

    try {
      const response = await deckService.getDeckFullDetails(deckId);
      const deckData = response.data.deck;

      // Update current deck ID for dropdown "Current" badge
      setCurrentDeckId(deckId);

      // Update deck and news list (left panel only)
      setDeck(deckData);
      const news = deckData.newsItems || [];
      setNewsList(news);
      // Reset order when switching decks
      const newsIds = news.map(n => n._id);
      setOriginalNewsOrder(newsIds);
      setCurrentNewsOrder(newsIds);
      setIsMobileReorderMode(false);

      // Update news decks for selected news to show correct "Current" badge in dropdown
      if (currentNewsId) {
        const newsDecksResponse = await newsService.getNewsDecks(currentNewsId);
        const decks = newsDecksResponse.data?.decks || newsDecksResponse.data || [];
        setNewsDecks(prev => ({ ...prev, [currentNewsId]: decks }));
      }

      // Try to keep the selected news visible if it exists in the new deck's list
      if (currentNewsId) {
        const newsInNewDeck = deckData.newsItems?.find(n => n._id === currentNewsId);
        if (newsInNewDeck) {
          setSelectedNews(newsInNewDeck);
        } else {
          // If news not in new deck, select first news
          const firstNews = deckData.newsItems && deckData.newsItems.length > 0
            ? deckData.newsItems[0]
            : null;
          setSelectedNews(firstNews);
        }
      }
    } catch (err) {
      setError('Failed to load deck details');
      console.error('Error fetching deck details:', err);
    }
  };

  useEffect(() => {
    const key = `${id}-${location.state?.selectedNewsId || ''}`;
    if (lastProcessedDeckId.current === key) return;
    lastProcessedDeckId.current = key;
    fetchDeckDetails();
  }, [id, location.state?.selectedNewsId]);

  useEffect(() => {
    const updateColumnHeight = () => {
      if (middleColumnRef.current) {
        const height = middleColumnRef.current.offsetHeight;
        // Set column height if we have selected news (deck is not empty)
        // When in add news mode, use the middle column height or a default height
        if (selectedNews && height > 100) {
          setColumnHeight(height);
        } else if (isAddNewsMode) {
          // In add news mode, use a fixed default height
          setColumnHeight(600);
        } else {
          setColumnHeight(null);
        }
      }
    };

    // Small delay to ensure NewsCard is fully rendered
    const timer = setTimeout(updateColumnHeight, 100);

    window.addEventListener('resize', updateColumnHeight);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', updateColumnHeight);
    };
  }, [selectedNews, isAddNewsMode]);

  // Auto-scroll to right column on mobile when entering add news mode
  useEffect(() => {
    if (isAddNewsMode && rightColumnRef.current && window.innerWidth <= 900) {
      rightColumnRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [isAddNewsMode]);

  // Auto-scroll to middle column when news is selected (especially useful on mobile)
  useEffect(() => {
    if (selectedNews && middleColumnRef.current) {
      // Only scroll on mobile or when middle column is not fully visible
      const rect = middleColumnRef.current.getBoundingClientRect();
      const isVisible = rect.top >= 0 && rect.bottom <= window.innerHeight;

      if (!isVisible || window.innerWidth <= 900) {
        middleColumnRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, [selectedNews]);

  const fetchDeckDetails = async () => {
    try {
      setLoading(true);
      const response = await deckService.getDeckFullDetails(id);

      const deckData = response.data.deck;
      setDeck(deckData);
      const news = deckData.newsItems || [];
      setNewsList(news);
      // Initialize original and current order
      const newsIds = news.map(n => n._id);
      setOriginalNewsOrder(newsIds);
      setCurrentNewsOrder(newsIds);

      // Check if there's a selectedNewsId from navigation state
      const selectedNewsId = location.state?.selectedNewsId;

      // Select the specified news or first news by default
      if (news.length > 0) {
        const newsToSelect = selectedNewsId
          ? news.find(n => n._id === selectedNewsId) || news[0]
          : news[0];
        setSelectedNews(newsToSelect);
      }
    } catch (err) {
      setError('Failed to load deck details');
      console.error('Error fetching deck details:', err);
    } finally {
      setLoading(false);
    }
  };

  const refreshNewsList = async (keepSelectedNewsId = null, fetchFromInshorts = false) => {
    try {
      setNewsListLoading(true);
      const params = fetchFromInshorts ? { fetchInshortsDeck: 'true' } : {};
      const response = await deckService.getDeckFullDetails(currentDeckId, params);
      const deckData = response.data.deck;
      const news = deckData.newsItems || [];
      setNewsList(news);
      // Reset order when refreshing
      const newsIds = news.map(n => n._id);
      setOriginalNewsOrder(newsIds);
      setCurrentNewsOrder(newsIds);
      setIsMobileReorderMode(false);

      // Update deck data if fetching from Inshorts
      if (fetchFromInshorts) {
        setDeck(deckData);
      }

      // Keep the selected news by ID and update with fresh data
      if (news.length > 0) {
        const selectedId = keepSelectedNewsId || selectedNews?._id;
        const updatedSelectedNews = news.find(n => n._id === selectedId);
        if (updatedSelectedNews) {
          setSelectedNews(updatedSelectedNews);
        } else {
          setSelectedNews(news[0]);
        }
      }
    } catch (err) {
      console.error('Error refreshing news list:', err);
    } finally {
      setNewsListLoading(false);
    }
  };

  const refreshHistoryOnly = async () => {
    try {
      const response = await deckService.getDeckFullDetails(currentDeckId);
      const deckData = response.data.deck;
      setDeck(deckData);
    } catch (err) {
      console.error('Error refreshing deck history:', err);
    }
  };

  const handleNewsSelect = (news) => {
    setSelectedNews(news);
  };

  const fetchNewsDecks = async (newsId) => {
    try {
      const response = await newsService.getNewsDecks(newsId);
      const decks = response.data?.decks || response.data || response || [];
      setNewsDecks(prev => ({
        ...prev,
        [newsId]: Array.isArray(decks) ? decks : []
      }));
      lastProcessedNewsId.current = newsId;
    } catch (err) {
      console.error('Error fetching news decks:', err);
    }
  };

  // Only fetch decks for the initially selected news (first news by default)
  useEffect(() => {
    if (selectedNews && lastProcessedNewsId.current !== selectedNews._id) {
      fetchNewsDecks(selectedNews._id);
    }
  }, [selectedNews]);

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  const getUserName = (user) => {
    if (!user) return 'Unknown';
    // If user is a string (email or user ID), try to extract email name
    if (typeof user === 'string') {
      return user;
    }
    // If user is an object with firstName/lastName
    if (user.firstName || user.lastName) {
      return `${user.firstName || ''} ${user.lastName || ''}`.trim();
    }
    // Otherwise use email
    return user.email || 'Unknown';
  };

  // Generate news from URL
  const handleGenerateNews = async (e) => {
    e.preventDefault();
    if (!newsUrl || !newsUrl.trim()) {
      setNewsGenerationError('Please enter a URL');
      return;
    }

    let finalUrl = newsUrl.trim();
    if (!finalUrl.startsWith('http://') && !finalUrl.startsWith('https://')) {
      finalUrl = 'https://' + finalUrl;
    }

    setGeneratingNews(true);
    setNewsGenerationError(null);
    setNewsGenerationNotice(null);

    try {
      const response = await newsService.generateNews(finalUrl, undefined, {
        onClientFetchStart: () =>
          setNewsGenerationNotice("This publisher blocks our server - reading the article from your browser instead..."),
      });
      if (response.success) {
        setGeneratedNews(response.data.news);
      }
    } catch (err) {
      // A browser-side fetch failure is a plain Error with a ready message;
      // anything from the server arrives as an axios error
      let errorMessage = err.response?.data?.message || err.message || 'Failed to generate news';

      // Show user-friendly message for nitter instance failures
      if (errorMessage.includes('All nitter instances failed') || errorMessage.includes('HTTP 502')) {
        errorMessage = "We're unable to load this article as the source website has access restrictions.";
      }

      setNewsGenerationError(errorMessage);
    } finally {
      setGeneratingNews(false);
      setNewsGenerationNotice(null);
    }
  };

  // Validate URL for button enable/disable
  const isValidUrl = () => {
    if (!newsUrl || !newsUrl.trim()) return false;
    const trimmedUrl = newsUrl.trim();
    return trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://') || trimmedUrl.includes('.');
  };

  // Add generated news to deck
  const handleAddNewsToDeck = async () => {
    if (!generatedNews) return;

    try {
      setAddingGeneratedNews(true);
      const newsId = generatedNews._id;
      const response = await deckService.addNewsToDeck(currentDeckId, newsId);
      if (response.success) {
        // Select the newly added news first
        setSelectedNews(generatedNews);
        const newNewsId = generatedNews._id;
        setGeneratedNews(null);
        setNewsUrl('');
        // Refresh left section and history, keeping the newly added news selected
        await refreshNewsList(newNewsId);
        await refreshHistoryOnly();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add news to deck');
    } finally {
      setAddingGeneratedNews(false);
    }
  };

  // Add search result news to deck
  const handleSearchResultAddToDeck = async (newsItem) => {
    if (addingNewsId) return; // Prevent multiple clicks

    try {
      setAddingNewsId(newsItem._id);
      const response = await deckService.addNewsToDeck(currentDeckId, newsItem._id);
      if (response.success) {
        // Select the newly added news first
        setSelectedNews(newsItem);
        const newNewsId = newsItem._id;
        // Refresh left section and history, keeping the newly added news selected
        await refreshNewsList(newNewsId);
        await refreshHistoryOnly();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add news to deck');
    } finally {
      setAddingNewsId(null);
    }
  };

  // Remove news from deck
  const handleRemoveNewsFromDeck = async (newsId, e) => {
    if (e) {
      e.stopPropagation();
    }

    // Show inline confirmation
    setConfirmRemoveNewsId(newsId);
  };

  // Confirm remove news from deck
  const handleConfirmRemove = async (newsId, e) => {
    if (e) {
      e.stopPropagation();
    }

    try {
      await deckService.removeNewsFromDeck(currentDeckId, newsId);
      setConfirmRemoveNewsId(null);
      // Only refresh history section, not left section
      await refreshHistoryOnly();
      // Remove the news from local newsList and select first news
      setNewsList(prev => {
        const filtered = prev.filter(n => n._id !== newsId);
        // Select first news if the selected news was removed
        if (selectedNews?._id === newsId) {
          setSelectedNews(filtered.length > 0 ? filtered[0] : null);
        }
        return filtered;
      });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to remove news from deck');
      setConfirmRemoveNewsId(null);
    }
  };

  // Cancel remove
  const handleCancelRemove = (e) => {
    if (e) {
      e.stopPropagation();
    }
    setConfirmRemoveNewsId(null);
  };

  // Drag and drop handlers
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) return;

    const newOrder = [...currentNewsOrder];
    const [draggedItem] = newOrder.splice(draggedIndex, 1);
    newOrder.splice(dropIndex, 0, draggedItem);
    setCurrentNewsOrder(newOrder);
    setReorderingItem(draggedItem);
    setDraggedIndex(null);
    setTimeout(() => setReorderingItem(null), 500);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  // Move news item up
  const handleMoveUp = (index) => {
    if (index === 0) return;
    const newOrder = [...currentNewsOrder];
    [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
    setCurrentNewsOrder(newOrder);
    setReorderingItem(newOrder[index - 1]);
    setTimeout(() => setReorderingItem(null), 500);
  };

  // Move news item down
  const handleMoveDown = (index) => {
    if (index === currentNewsOrder.length - 1) return;
    const newOrder = [...currentNewsOrder];
    [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
    setCurrentNewsOrder(newOrder);
    setReorderingItem(newOrder[index + 1]);
    setTimeout(() => setReorderingItem(null), 500);
  };

  // Save order
  const handleSaveOrder = async () => {
    try {
      setSavingOrder(true);
      const response = await deckService.reorderNews(currentDeckId, currentNewsOrder);
      if (response.success) {
        // Update original order to match current
        setOriginalNewsOrder([...currentNewsOrder]);
        setIsMobileReorderMode(false);
        // Refresh the news list to get the updated order, keeping selected news
        await refreshNewsList(selectedNews?._id);
        await refreshHistoryOnly();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save order');
    } finally {
      setSavingOrder(false);
    }
  };

  // Reset order
  const handleResetOrder = () => {
    setCurrentNewsOrder([...originalNewsOrder]);
    setIsMobileReorderMode(false);
  };

  // Check if order has changed
  const hasOrderChanged = () => {
    return JSON.stringify(originalNewsOrder) !== JSON.stringify(currentNewsOrder);
  };

  // Handle create deck status (draft -> created)
  const handleCreateDeck = async () => {
    // Validate: need at least 1 news to create deck
    if (newsList.length === 0) {
      setCreateDeckError('Add at least 1 news to create the deck');
      setTimeout(() => setCreateDeckError(null), 3000);
      return;
    }

    try {
      setCreatingDeck(true);
      const response = await deckService.createDeckStatus(currentDeckId);
      if (response.success) {
        setDeck(response.data.deck);
        await refreshHistoryOnly();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create deck');
    } finally {
      setCreatingDeck(false);
    }
  };

  // Handle publish deck status (created -> published)
  const handlePublishDeck = () => {
    // Reset any previous error
    setError(null);
    // Pre-fill the form with existing deck values
    setPublishPosition(deck.position || '');
    // Convert timestamp to datetime-local format
    if (deck.durationFrom) {
      const date = new Date(deck.durationFrom);
      setPublishDurationFromDateTime(`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}T${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`);
    }
    if (deck.durationTo) {
      const date = new Date(deck.durationTo);
      setPublishDurationToDateTime(`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}T${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`);
    }
    setShowPublishForm(true);
  };

  // Confirm publish deck with form values
  const handleConfirmPublish = async () => {
    // Validate inputs
    if (!publishPosition || !publishDurationFromDateTime || !publishDurationToDateTime) {
      setError('All fields are required');
      return;
    }

    try {
      setPublishingDeck(true);
      setError(null);
      // Convert datetime to timestamp
      const fromTimestamp = new Date(publishDurationFromDateTime).getTime();
      const toTimestamp = new Date(publishDurationToDateTime).getTime();

      const response = await deckService.publishDeckStatus(
        currentDeckId,
        publishPosition,
        fromTimestamp,
        toTimestamp
      );
      if (response.success) {
        setDeck(response.data.deck);
        setShowPublishForm(false);
        await refreshHistoryOnly();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to publish deck');
    } finally {
      setPublishingDeck(false);
    }
  };

  // Cancel publish form
  const handleCancelPublish = () => {
    setShowPublishForm(false);
  };

  // Handle update deck details
  const handleUpdateDeck = async () => {
    try {
      setUpdatingDeck(true);
      const response = await deckService.updateDeck(
        currentDeckId,
        deck.name,
        deck.topic,
        deck.durationFrom,
        deck.durationTo,
        deck.identityKey,
        deck.identityId,
        deck.assets,
        deck.priority,
        deck.position,
        deck.coverType,
        deck.langCode
      );
      if (response.success) {
        setDeck(response.data.deck);
        await refreshHistoryOnly();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update deck');
    } finally {
      setUpdatingDeck(false);
    }
  };

  // Handle delete deck
  const handleDeleteDeck = () => {
    setShowDeckDeleteConfirm(true);
  };

  // Confirm delete deck
  const handleConfirmDeleteDeck = async () => {
    try {
      setDeletingDeck(true);
      const response = await deckService.archiveDeck(currentDeckId);
      if (response.success) {
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete deck');
      setShowDeckDeleteConfirm(false);
    } finally {
      setDeletingDeck(false);
    }
  };

  // Cancel delete deck
  const handleCancelDeleteDeck = () => {
    setShowDeckDeleteConfirm(false);
  };

  // Cancel mobile reorder mode
  const handleCancelMobileReorder = () => {
    setIsMobileReorderMode(false);
  };

  // Debounced search for news
  useEffect(() => {
    const timeoutId = setTimeout(async () => {
      if (newsSearch.trim()) {
        setSearchLoading(true);
        try {
          const response = await newsService.getMyNews({ search: newsSearch.trim() });
          if (response.success) {
            const results = response.data || [];
            setSearchResults(results);
            // Fetch decks for each search result
            const deckPromises = results.map(news =>
              newsService.getNewsDecks(news._id)
                .then(res => ({
                  newsId: news._id,
                  decks: res.data?.decks || res.data || []
                }))
                .catch(() => ({ newsId: news._id, decks: [] }))
            );
            const decksResults = await Promise.all(deckPromises);
            const decksMap = {};
            decksResults.forEach(({ newsId, decks }) => {
              decksMap[newsId] = decks;
            });
            setSearchResultsDecks(decksMap);
          }
        } catch (err) {
          setSearchResults([]);
          setSearchResultsDecks({});
        } finally {
          setSearchLoading(false);
        }
      } else {
        setSearchResults([]);
        setSearchResultsDecks({});
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [newsSearch]);

  if (loading) {
    return (
      <div className="deck-detail-page">
        <div className="deck-detail-loading">
          <Loader />
        </div>
      </div>
    );
  }

  if (error || !deck) {
    return (
      <div className="deck-detail-page">
        <div className="deck-detail-error">
          <p>{error || 'Deck not found'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="deck-detail-page">
      <div className="deck-detail-container">
        {/* Top Section - Deck Metadata */}
        <div className={`deck-metadata-div deck-metadata-div--${deck.status?.toLowerCase() || 'draft'}`}>
          {/* Cover Image - Carousel */}
          <DeckCardMedia assets={deck.assets || []} coverType={deck.coverType} />

          {/* Left Content Area - Desktop */}
          <div className="deck-metadata-left">
            {/* Row 1: Deck Name and Language Code Only */}
            <div className="deck-metadata-row deck-metadata-row-1">
              <span className="deck-metadata-name">{deck.name}</span>
              {deck.langCode && (
                <span className="deck-metadata-info-item">
                  <Globe size={14} />
                  <span>{deck.langCode.toUpperCase()}</span>
                </span>
              )}
              <button onClick={() => refreshNewsList(null, true)} className="btn-refresh btn-refresh-metadata" title="Refresh" disabled={newsListLoading || deck.status === 'modified'}>
                <RefreshCw size={14} className={newsListLoading ? 'spinning' : ''} />
              </button>
            </div>

            {/* Row 2: Deck Details */}
            <div className="deck-metadata-row deck-metadata-row-2">
              {deck.topic && (
                <span className="deck-metadata-info-item">
                  <Hash size={14} />
                  <span>{deck.topic}</span>
                </span>
              )}
              {(deck.durationFrom || deck.durationTo) && (
                <span className="deck-metadata-info-item">
                  <CalendarIcon size={14} />
                  <span>
                    {deck.durationFrom ? formatDateTime(deck.durationFrom) : ''} - {deck.durationTo ? formatDateTime(deck.durationTo) : ''}
                  </span>
                </span>
              )}
              {deck.position && (
                <span className="deck-metadata-info-item">
                  <ArrowUp size={14} />
                  <span>Pos: {deck.position}</span>
                </span>
              )}
              {deck.priority && (
                <span className="deck-metadata-info-item">
                  <Star size={14} />
                  <span>{deck.priority}</span>
                </span>
              )}
            </div>

            {/* Row 3: Created By and Created Time Only */}
            <div className="deck-metadata-row deck-metadata-row-3">
              <span className="deck-metadata-info-item">
                <UserIcon size={14} />
                <span>{getUserName(deck.createdBy.email)}</span>
              </span>
              <span className="deck-metadata-info-item">
                <Clock size={14} />
                <span>{formatDateTime(deck.createdAt)}</span>
              </span>
            </div>
          </div>

          {/* Mobile Metadata Rows - 6 rows layout */}
          <div className="deck-metadata-mobile">
            {/* Row 1: Cover Asset Image/Media */}
            <div className="deck-metadata-mobile-row deck-metadata-mobile-row-1">
              <DeckCardMedia assets={deck.assets || []} coverType={deck.coverType} />
            </div>

            {/* Row 2: Deck Name with refresh icon */}
            <div className="deck-metadata-mobile-row deck-metadata-mobile-row-2">
              <span className="deck-metadata-name-mobile">{deck.name}</span>
              <button onClick={() => refreshNewsList(null, true)} className="btn-refresh btn-refresh-metadata-mobile" title="Refresh" disabled={newsListLoading || deck.status === 'modified'}>
                <RefreshCw size={14} className={newsListLoading ? 'spinning' : ''} />
              </button>
            </div>

            {/* Row 3: Language and category */}
            <div className="deck-metadata-mobile-row deck-metadata-mobile-row-3">
              {deck.langCode && (
                <span className="deck-metadata-info-item">
                  <Globe size={14} />
                  <span>{deck.langCode.toUpperCase()}</span>
                </span>
              )}
              {deck.topic && (
                <span className="deck-metadata-info-item">
                  <Hash size={14} />
                  <span>{deck.topic}</span>
                </span>
              )}
            </div>

            {/* Row 4: From and to date */}
            <div className="deck-metadata-mobile-row deck-metadata-mobile-row-4">
              {(deck.durationFrom || deck.durationTo) && (
                <span className="deck-metadata-info-item">
                  <CalendarIcon size={14} />
                  <span>
                    {deck.durationFrom ? formatDateTime(deck.durationFrom) : ''} - {deck.durationTo ? formatDateTime(deck.durationTo) : ''}
                  </span>
                </span>
              )}
            </div>

            {/* Row 5: Priority & position */}
            <div className="deck-metadata-mobile-row deck-metadata-mobile-row-5">
              {deck.position && (
                <span className="deck-metadata-info-item">
                  <ArrowUp size={14} />
                  <span>Pos: {deck.position}</span>
                </span>
              )}
              {deck.priority && (
                <span className="deck-metadata-info-item">
                  <Star size={14} />
                  <span>{deck.priority}</span>
                </span>
              )}
            </div>

            {/* Row 6: Created By and time */}
            <div className="deck-metadata-mobile-row deck-metadata-mobile-row-6">
              <span className="deck-metadata-info-item">
                <UserIcon size={14} />
                <span>{getUserName(deck.createdBy.email)}</span>
              </span>
              <span className="deck-metadata-info-item">
                <Clock size={14} />
                <span>{formatDateTime(deck.createdAt)}</span>
              </span>
            </div>
          </div>

          {/* Vertical Divider */}
          <div className="deck-metadata-divider"></div>

          {/* Right Action Area */}
          <div className="deck-metadata-actions">
            {/* Status Badge */}
            <div className="deck-metadata-action-row deck-metadata-action-row--top">
              <span className={`deck-status-badge deck-status-badge--${deck.status?.toLowerCase() || 'draft'}`}>
                {deck.status ? 'Status → '+ deck.status.toUpperCase() : 'DRAFT'}
              </span>
            </div>

            {/* Action Buttons - Side by Side */}
            <div className="deck-metadata-action-row deck-metadata-action-row--buttons">
              {deck.status === 'draft' && (
                <>
                  <button onClick={handleCreateDeck} className="deck-action-btn btn-create-deck" disabled={creatingDeck}>
                    {creatingDeck ? 'Creating...' : (
                      <>
                        <Plus size={14} />
                        Create Deck
                      </>
                    )}
                  </button>
                  <button onClick={handleDeleteDeck} className="deck-action-btn btn-archive-deck" disabled={deletingDeck}>
                    {deletingDeck ? 'Archiving...' : (
                      <>
                        <Archive size={14} />
                        Archive Deck
                      </>
                    )}
                  </button>
                </>
              )}
              {deck.status === 'modified' && (
                <>
                  <button onClick={handleUpdateDeck} className="deck-action-btn btn-update-deck" disabled={updatingDeck}>
                    {updatingDeck ? 'Syncing...' : (
                      <>
                        <RotateCcw size={14} />
                        Sync Deck
                      </>
                    )}
                  </button>
                  <button onClick={handleDeleteDeck} className="deck-action-btn btn-delete-deck" disabled={deletingDeck}>
                    {deletingDeck ? 'Deleting...' : (
                      <>
                        <Trash size={14} />
                        Delete Deck
                      </>
                    )}
                  </button>
                </>
              )}
              {deck.status === 'created' && (
                <>
                  <button onClick={handlePublishDeck} className="deck-action-btn btn-publish-deck" disabled={publishingDeck}>
                    {publishingDeck ? 'Going Live...' : (
                      <>
                        <Upload size={14} />
                        Go Live
                      </>
                    )}
                  </button>
                  <button onClick={handleDeleteDeck} className="deck-action-btn btn-delete-deck" disabled={deletingDeck}>
                    {deletingDeck ? 'Deleting...' : (
                      <>
                        <Trash size={14} />
                        Delete Deck
                      </>
                    )}
                  </button>
                </>
              )}
              {deck.status === 'published' && (
                <button onClick={handleDeleteDeck} className="deck-action-btn btn-delete-deck" disabled={deletingDeck}>
                  {deletingDeck ? 'Deleting...' : (
                    <>
                      <Trash size={14} />
                      Delete Deck
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Bottom Section - Deck Content */}
        <div className="deck-content-div">
          {/* Main Content - Three Column Layout */}
          <div className="deck-detail-main-content">
          {/* Left Column - News List */}
          <div className="deck-detail-left-column" style={columnHeight ? { height: `${columnHeight}px` } : {}}>
            <div className="deck-news-list-header">
              <div className="deck-news-list-header-top">
                <div className="deck-news-list-title">
                  <h2>News List</h2>
                  <span className="deck-news-total-count">({newsList.length}) </span>
                </div>
                {hasOrderChanged() ? (
                  <div className="deck-news-list-header-right deck-news-list-header-right--row">
                    <button onClick={handleSaveOrder} className="link-save-order" disabled={savingOrder}>
                      {savingOrder ? 'Saving...' : 'Save'}
                    </button>
                    <button onClick={handleResetOrder} className="link-reset-order">
                      Reset
                    </button>
                  </div>
                ) : isMobileReorderMode ? (
                  <div className="deck-news-list-header-right deck-news-list-header-right--row">
                    <button onClick={handleCancelMobileReorder} className="link-cancel-order">
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div className="deck-news-list-header-right deck-news-list-header-right--row">
                    {deck.status !== 'published' && (
                      <button
                        onClick={() => setIsAddNewsMode(true)}
                        className="btn-add-news-bottom"
                        disabled={deck.status === 'archived' || deck.status === 'deleted' || showPublishForm}
                      >
                        + Add Shorts
                      </button>
                    )}
                    {(deck.status !== 'published' || deck.status === 'published') && (
                      <button
                        onClick={() => setIsMobileReorderMode(true)}
                        className="btn-reorder-news-bottom"
                        disabled={deck.status === 'archived' || deck.status === 'deleted' || newsList.length === 0 || showPublishForm}
                      >
                        ReOrder Shorts
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="deck-news-list-scroll-container">
              {/* Publish Form - Replaces news list */}
              {showPublishForm ? (
                <div className="deck-publish-form-container">
                  {error && <div className="deck-publish-form-error">{error}</div>}
                  <div className="deck-publish-form-fields">
                    <div className="deck-publish-form-row">
                      <div className="deck-publish-form-field">
                        <label className="deck-publish-form-label">Start Time</label>
                        <input
                          type="datetime-local"
                          className="deck-publish-form-input"
                          value={publishDurationFromDateTime}
                          onChange={(e) => setPublishDurationFromDateTime(e.target.value)}
                          disabled={publishingDeck}
                        />
                      </div>
                      <div className="deck-publish-form-field">
                        <label className="deck-publish-form-label">Expiry Time</label>
                        <input
                          type="datetime-local"
                          className="deck-publish-form-input"
                          value={publishDurationToDateTime}
                          onChange={(e) => setPublishDurationToDateTime(e.target.value)}
                          disabled={publishingDeck}
                        />
                      </div>
                    </div>
                    <div className="deck-publish-form-field">
                      <label className="deck-publish-form-label">Position</label>
                      <input
                        type="number"
                        className="deck-publish-form-input"
                        value={publishPosition}
                        onChange={(e) => setPublishPosition(e.target.value)}
                        placeholder="Enter position"
                        disabled={publishingDeck}
                      />
                    </div>
                  </div>
                  <div className="deck-publish-form-buttons">
                    <button
                      className="deck-publish-form-cancel"
                      onClick={handleCancelPublish}
                      disabled={publishingDeck}
                    >
                      Cancel
                    </button>
                    <button
                      className="deck-publish-form-confirm"
                      onClick={handleConfirmPublish}
                      disabled={publishingDeck}
                    >
                      {publishingDeck ? 'Going Live...' : 'Go Live'}
                    </button>
                  </div>
                  {publishingDeck && (
                    <div className="deck-publish-form-loader">
                      <Loader />
                    </div>
                  )}
                </div>
              ) : (
                <>
                  {/* Deck Delete Confirmation Overlay */}
                  {showDeckDeleteConfirm && (
                    <div className="deck-delete-confirm-overlay" onClick={handleCancelDeleteDeck}>
                      <span className="deck-delete-confirm-text">
                        {deck.status === 'draft' ? 'Archive Deck?' : 'Delete Deck?'}
                      </span>
                      <div className="deck-delete-confirm-buttons">
                        <button
                          className="deck-delete-confirm-yes"
                          onClick={handleConfirmDeleteDeck}
                          disabled={deletingDeck}
                        >
                          Yes
                        </button>
                        <button
                          className="deck-delete-confirm-no"
                          onClick={handleCancelDeleteDeck}
                        >
                          No
                        </button>
                      </div>
                    </div>
                  )}

                  {newsList.length === 0 ? (
                    <div className="deck-news-list-empty">
                      <FileText size={32} />
                      <p>No news items in this deck</p>
                      {createDeckError && (
                        <div className="deck-create-error">
                          {createDeckError}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="deck-news-list">
                      {currentNewsOrder.map((newsId, index) => {
                        const news = newsList.find(n => n._id === newsId);
                        if (!news) return null;

                        return (
                          <div
                            key={news._id}
                            className={`deck-news-item ${selectedNews?._id === news._id ? 'deck-news-item--selected' : ''} ${draggedIndex === index ? 'deck-news-item--dragging' : ''} ${clickedNewsId === news._id ? 'deck-news-item--clicked' : ''} ${reorderingItem === news._id ? 'deck-news-item--reordering' : ''}`}
                            draggable
                            onDragStart={(e) => handleDragStart(e, index)}
                            onDragOver={handleDragOver}
                            onDrop={(e) => handleDrop(e, index)}
                            onDragEnd={handleDragEnd}
                            onClick={(e) => {
                              setClickedNewsId(news._id);
                              handleNewsSelect(news);
                            }}
                            onAnimationEnd={() => setClickedNewsId(null)}
                          >
                            <div className="deck-news-item-header">
                              <span className="deck-news-item-headline">{news.headline || news.heading}</span>
                            </div>

                            <div className="deck-news-item-meta">
                              <span className="deck-news-item-time">
                                <Clock size={12} />
                                {formatDateTime(news.addedAt)}
                              </span>
                              <span className="deck-news-item-user">
                                <UserIcon size={12} />
                                {shortenEmail(news.addedBy || getUserName(news.userId))}
                              </span>
                              {isMobileReorderMode ? (
                                <div className="deck-news-item-mobile-reorder-buttons">
                                  <button
                                    className="deck-news-item-move-up"
                                    onClick={(e) => { e.stopPropagation(); handleNewsSelect(news); handleMoveUp(index); }}
                                    disabled={index === 0}
                                    title="Move up"
                                  >
                                    <ChevronUp size={14} />
                                  </button>
                                  <button
                                    className="deck-news-item-move-down"
                                    onClick={(e) => { e.stopPropagation(); handleNewsSelect(news); handleMoveDown(index); }}
                                    disabled={index === currentNewsOrder.length - 1}
                                    title="Move down"
                                  >
                                    <ChevronDown size={14} />
                                  </button>
                                </div>
                              ) : (
                                <button
                                  className={`deck-news-item-remove ${deck.status === 'archived' || deck.status === 'deleted' ? 'deck-news-item-remove--disabled' : ''}`}
                                  onClick={(e) => handleRemoveNewsFromDeck(news._id, e)}
                                  title="Remove from deck"
                                  disabled={deck.status === 'archived' || deck.status === 'deleted'}
                                >
                                  <Trash size={14} />
                                </button>
                              )}
                            </div>

                            {/* Confirmation Overlay */}
                            {!isMobileReorderMode && confirmRemoveNewsId === news._id && (
                              <div className="deck-news-item-confirm-overlay" onClick={(e) => e.stopPropagation()}>
                                <span className="deck-news-item-confirm-text">Remove news from Deck?</span>
                                <div className="deck-news-item-confirm-buttons">
                                  <button
                                    className="deck-news-item-confirm-yes"
                                    onClick={(e) => handleConfirmRemove(news._id, e)}
                                  >
                                    Yes
                                  </button>
                                  <button
                                    className="deck-news-item-confirm-no"
                                    onClick={handleCancelRemove}
                                  >
                                    No
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Middle Column - Selected News Card */}
          <div className="deck-detail-middle-column" ref={middleColumnRef}>
            {selectedNews ? (
              <NewsCard
                news={selectedNews}
                showEdit={false}
                showAddToDeckButton={false}
                showEditShortsButton={true}
                showPublishButton={true}
                onEnterEditMode={(newsId) => {
                  window.open(`/dashboard/news/${newsId}?edit=true&deckId=${currentDeckId}`, '_blank');
                }}
                newsDecks={newsDecks[selectedNews?._id] || []}
                currentDeckId={currentDeckId}
                onDeckSelect={handleDeckSelect}
                onGoLiveSuccess={() => fetchDeckDetails()}
                onClick={(e) => {
                  const isModifierPressed = e.ctrlKey || e.metaKey || e.shiftKey || e.altKey;
                  if (isModifierPressed) {
                    window.open(`/dashboard/news/${selectedNews._id}?deckId=${currentDeckId}`, '_blank');
                  } else {
                    navigate(`/dashboard/news/${selectedNews._id}?deckId=${currentDeckId}`);
                  }
                }}
              />
            ) : (
              <div className="deck-news-empty-state">
                <FileText size={48} />
                <h3>Select a news item</h3>
                <p>Click on any news item from list to view its details</p>
              </div>
            )}
          </div>

          {/* Right Column - History / Add News */}
          <div className="deck-detail-right-column" ref={rightColumnRef} style={columnHeight ? { height: `${columnHeight}px` } : {}}>
            {isAddNewsMode ? (
              <div className="deck-add-news-section">
                <div className="deck-news-decks-header">
                  <div className="search-input-wrapper header-search">
                    <Search size={16} className="search-icon" />
                    <input
                      type="text"
                      className="search-input"
                      placeholder="Search news..."
                      value={newsSearch}
                      onChange={(e) => setNewsSearch(e.target.value)}
                    />
                  </div>
                  <button onClick={() => setIsAddNewsMode(false)} className="btn-close-add-news">
                    <X size={18} color="#F44336" />
                  </button>
                </div>
                {newsGenerationError && <div className="add-news-error">{newsGenerationError}</div>}
                {newsGenerationNotice && <div className="add-news-notice">{newsGenerationNotice}</div>}
                <div className="add-news-url-form">
                  <input
                    type="url"
                    className="add-news-url-input"
                    placeholder="Enter article URL..."
                    value={newsUrl}
                    onChange={(e) => {
                      setNewsUrl(e.target.value);
                      setNewsGenerationError(null);
                    }}
                  />
                  <button
                    onClick={handleGenerateNews}
                    disabled={generatingNews || !isValidUrl()}
                    className="btn-generate-news"
                  >
                    {generatingNews ? 'Generating...' : 'Generate Short'}
                  </button>
                </div>
                <div className="add-news-content">
                {generatingNews && (
                  <div className="add-news-loading">
                    <Loader />
                  </div>
                )}
                {generatedNews && (
                  <div className="add-news-generated">
                    <NewsCard
                      news={generatedNews}
                      showEdit={false}
                      showAddToDeckButton={false}
                      showPublishButton={false}
                      currentDeckId={currentDeckId}
                      onDeckSelect={handleDeckSelect}
                      customImageButton={
                        isNewsInDeck(generatedNews._id) ? (
                          <button
                            disabled
                            className="card-custom-image-btn btn-added"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Check size={14} />
                            Added
                          </button>
                        ) : (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAddNewsToDeck();
                            }}
                            className="card-custom-image-btn"
                            disabled={addingGeneratedNews}
                          >
                            <Plus size={14} />
                            {addingGeneratedNews ? 'Adding...' : 'Add'}
                          </button>
                        )
                      }
                    />
                  </div>
                )}
                {!generatingNews && !generatedNews && searchLoading && (
                  <div className="add-news-loading">
                    <Loader />
                  </div>
                )}
                {!generatingNews && !generatedNews && searchResults.length > 0 && (
                  <div className="add-news-results">
                    {searchResults.map((news) => (
                      <NewsCard
                        key={news._id}
                        news={news}
                        showEdit={false}
                        showAddToDeckButton={false}
                        showPublishButton={true}
                        currentDeckId={currentDeckId}
                        onDeckSelect={handleDeckSelect}
                        newsDecks={searchResultsDecks[news._id] || []}
                        onGoLiveSuccess={() => fetchDeckDetails()}
                        customImageButton={
                          isNewsInDeck(news._id) ? (
                            <button
                              disabled
                              className="card-custom-image-btn btn-added"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Check size={14} />
                              Added
                            </button>
                          ) : (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSearchResultAddToDeck(news);
                              }}
                              className="card-custom-image-btn"
                              disabled={addingNewsId === news._id}
                            >
                              <Plus size={14} />
                              {addingNewsId === news._id ? 'Adding...' : 'Add'}
                            </button>
                          )
                        }
                      />
                    ))}
                  </div>
                )}
                {!generatingNews && !generatedNews && !searchLoading && searchResults.length === 0 && !newsSearch && (
                  <div className="add-news-placeholder">
                    <div className="placeholder-icon">🧾</div>
                    <p>Search or Generate News to Add in Deck</p>
                  </div>
                )}
                </div>
              </div>
            ) : deck.history && deck.history.length > 0 ? (
              <div className="deck-news-decks-section">
                <div className="deck-news-decks-header">
                  <HistoryIcon size={14} />
                  <span>Deck History</span>
                  <span className="deck-news-decks-count">{deck.history.length}</span>
                </div>
                <div className="deck-history-list">
                  {deck.history.slice().reverse().map((item, index) => {
                    let actionText = '';
                    let detailElement = null;

                    if (item.action === 'deck_drafted') {
                      actionText = 'Deck drafted.';
                    } else if (item.action === 'deck_created') {
                      actionText = 'Deck created.';
                    } else if (item.action === 'deck_modified') {
                      actionText = 'Deck modified.';
                    } else if (item.action === 'name_update') {
                      actionText = 'Name updated';
                      detailElement = (
                        <>
                          From <span className="deck-history-prev-value">'{item.previousValue}'</span> To <span className="deck-history-new-value">'{item.newValue}'</span>
                        </>
                      );
                    } else if (item.action === 'topic_update') {
                      actionText = 'Topic updated';
                      detailElement = (
                        <>
                          From <span className="deck-history-prev-value">'{item.previousValue}'</span> To <span className="deck-history-new-value">'{item.newValue}'</span>
                        </>
                      );
                    } else if (item.action === 'duration_update') {
                      actionText = 'Dates updated';

                      // Backend now sends formatted values like: "From: DD/MM/YY HH:MM AM, To: DD/MM/YY HH:MM PM"
                      // Or single values: "From: DD/MM/YY HH:MM AM" or "To: DD/MM/YY HH:MM PM"
                      // Or just: "From: (empty)" or "To: (empty)"

                      const formatHistoryValue = (value) => {
                        if (!value) return '';
                        // Replace "From: " and "To: " with colored spans
                        let result = value;
                        // Replace "From: " with colored span for what follows
                        result = result.replace(/From: /g, 'From ');
                        // Replace "To: " with colored span for what follows (but only if not part of a comma-separated value)
                        if (result.includes(', To: ')) {
                          result = result.replace(', To: ', ', To ');
                        } else {
                          result = result.replace('To: ', 'To ');
                        }
                        return result;
                      };

                      const prevValue = formatHistoryValue(item.previousValue);
                      const newValue = formatHistoryValue(item.newValue);

                      detailElement = (
                        <>
                          <span className="deck-history-prev-value">{prevValue}</span>
                          {' '}
                          <span className="deck-history-new-value">{newValue}</span>
                        </>
                      );
                    } else if (item.action === 'news_added') {
                      actionText = 'News added';
                      detailElement = item.newsData?.headline || 'Unknown';
                    } else if (item.action === 'news_removed') {
                      actionText = 'News removed';
                      detailElement = item.newsData?.headline || 'Unknown';
                    } else if (item.action === 'news_reordered') {
                      actionText = 'News order updated';
                    } else if (item.action === 'deck_published') {
                      actionText = 'Deck published.';
                    } else if (item.action === 'deck_archived') {
                      actionText = 'Deck archived';
                    } else if (item.action === 'deck_deleted') {
                      actionText = 'Deck deleted';
                    }

                    return (
                      <div key={index} className="deck-history-item">
                        <div className="deck-history-text">
                          <span className="deck-history-action">{actionText}</span>
                          {detailElement && <span className="deck-history-detail"> {detailElement}</span>}
                        </div>
                        <div className="deck-history-meta">
                          <span className="deck-history-time">
                            <Clock size={12} />
                            {formatDateTime(item.performedAt)}
                          </span>
                          <span className="deck-history-user">
                            <UserIcon size={12} />
                            {item.performedBy}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : null}
          </div>
        </div>
        </div>
      </div>
    </div>
  );
};

export default DeckDetail;
